import React from 'react';
import { WidthProvider, Responsive } from 'react-grid-layout';
import { Layout } from '../atoms-types/layout';
import { FormGroup, Button, Paper, useTheme, Box, Alert, CircularProgress, Snackbar } from '@mui/material';
import { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { endpointLink, websocketLink } from '../../index';
import { 
  useNotificationPreferences, 
} from '../../hooks/useNotifications';
import { useQueryClient } from '@tanstack/react-query';
import useWebSocket from '../../hooks/wsHook';
import { EmailNotificationCard } from '../cards/EmailNotificationCard';
import { SMSNotificationCard } from '../cards/SMSNotificationCard';
import { NotificationGroupCard } from '../cards/NotificationGroupCard';

const ResponsiveGridLayout = WidthProvider(Responsive);

const AccountSettingsGrid: React.FC = () => {
  const { getAccessTokenSilently, user } = useAuth0();

  const layout: Layout[] = [
    { i: 'a', x: 1, y: 0, w: 1, h: 1, minW: 1, minH: 1 },
    { i: 'b', x: 1, y: 3, w: 7, h: 8, minW: 1, minH: 2 },
  ];

  // React Query hooks for notification preferences
  const queryClient = useQueryClient();
  const { data: preferences, isLoading: preferencesLoading, error: preferencesError } = useNotificationPreferences();
  
  // WebSocket: listen for notification updates (emailVerified/smsVerified)
  const { messages: wsMessages } = useWebSocket<{ type: string; userId: string }>(websocketLink, ['notificationUpdate']);

  // Local state for snackbar and sync
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncMessage, setSyncMessage] = useState<{type: 'success' | 'error', text: string} | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const theme = useTheme();

  // React to websocket verification messages for this user
  useEffect(() => {
    if (!wsMessages?.length || !user?.sub) return;
    let shouldInvalidate = false;
    wsMessages.forEach(({ channel, message }) => {
      console.log('WebSocket message received:', { channel, message, userSub: user.sub });
      if (channel !== 'notificationUpdate') {
        console.log('Skipping message - wrong channel:', channel);
        return;
      }
      if (message?.userId !== user.sub) {
        console.log('Skipping message - wrong user:', message?.userId, 'expected:', user.sub);
        return;
      }
      if (message?.type === 'emailVerified' || message?.type === 'smsVerified') {
        console.log('Invalidating notification preferences due to verification:', message?.type);
        shouldInvalidate = true;
      }
    });
    if (shouldInvalidate) {
      console.log('Invalidating and refetching notification preferences query');
      // Invalidate and force refetch even if data is still fresh
      queryClient.invalidateQueries({ queryKey: ['notificationPreferences'] });
      queryClient.refetchQueries({ queryKey: ['notificationPreferences'] });
    }
  }, [wsMessages, user?.sub, queryClient]);

  // Listen for localStorage events from email verification page
  useEffect(() => {
    if (!user?.sub) return;

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'emailVerified' && e.newValue === 'true') {
        console.log('Email verification detected via localStorage, invalidating and refetching query');
        queryClient.invalidateQueries({ queryKey: ['notificationPreferences'] });
        queryClient.refetchQueries({ queryKey: ['notificationPreferences'] });
        // Clear the flag
        try {
          localStorage.removeItem('emailVerified');
        } catch (err) {
          console.warn('Could not clear emailVerified flag:', err);
        }
      }
    };

    // Check if email was verified while user was away
    const checkEmailVerified = () => {
      try {
        const emailVerified = localStorage.getItem('emailVerified');
        if (emailVerified === 'true') {
          console.log('Email verification flag found, invalidating and refetching query');
          queryClient.invalidateQueries({ queryKey: ['notificationPreferences'] });
          queryClient.refetchQueries({ queryKey: ['notificationPreferences'] });
          localStorage.removeItem('emailVerified');
        }
      } catch (err) {
        console.warn('Could not check emailVerified flag:', err);
      }
    };

    // Check immediately when component mounts
    checkEmailVerified();

    // Listen for storage events from other tabs
    window.addEventListener('storage', handleStorageChange);

    // Also check periodically in case the event didn't fire
    const interval = setInterval(checkEmailVerified, 2000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [user?.sub, queryClient]);

  // Handler for snackbar messages from child components
  const handleMessage = (message: string) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  const handleSyncAllUsers = async () => {
    setSyncLoading(true);
    setSyncMessage(null);
    
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: "https://odp.golf/audience",
          scope: "openid profile email",
        },
      });

      // Use the open util endpoint to bypass admin proxy auth
      console.log('Making request to:', `${endpointLink}util/sync-all-users-open`);
      
      const response = await fetch(`${endpointLink}util/sync-all-users-open`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();

      setSyncMessage({
        type: 'success',
        text: `Sync completed! ${data.result.synced}/${data.result.total} users synced successfully. ${data.result.errors} errors occurred.`
      });
    } catch (error: any) {
      console.error('Sync error:', error);
      
      // Check if it's a refresh token error
      if (error.message.includes('Missing Refresh Token') || error.message.includes('refresh_token')) {
        setSyncMessage({
          type: 'error',
          text: 'Authentication expired. Please refresh the page or log out and log back in to continue.'
        });
      } else {
        setSyncMessage({
          type: 'error',
          text: `Sync failed: ${error.message}`
        });
      }
    } finally {
      setSyncLoading(false);
    }
  };

  // Show loading state while preferences are being fetched
  if (preferencesLoading) {
    return (
      <ResponsiveGridLayout 
        className="layout" 
        layouts={{ lg: layout }} 
        breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480 }} 
        cols={{ lg: 3, md: 3, sm: 3, xs: 1 }}
        rowHeight={100}
        draggableCancel=".cancelSelectorName"
        isDraggable={false}
        isResizable={false}
        >
        <div key="a" style={{ minHeight: "500px" }} className='card-gauge'>
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', textAlign: 'center' }}>
            <h1>Account Settings</h1>
          </div>
        </div>
        <div key="b" style={{ minHeight: "500px" }} className='card-gauge'>
          <Paper elevation={3} style={{ margin: '32px auto', padding: 24 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
              <CircularProgress />
            </Box>
          </Paper>
        </div>
      </ResponsiveGridLayout>
    );
  }

  // Show error state if preferences failed to load
  if (preferencesError) {
    return (
      <ResponsiveGridLayout 
        className="layout" 
        layouts={{ lg: layout }} 
        breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480 }} 
        cols={{ lg: 3, md: 3, sm: 3, xs: 1 }}
        rowHeight={100}
        draggableCancel=".cancelSelectorName"
        isDraggable={false}
        isResizable={false}
        >
        <div key="a" style={{ minHeight: "500px" }} className='card-gauge'>
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', textAlign: 'center' }}>
            <h1>Account Settings</h1>
          </div>
        </div>
        <div key="b" style={{ minHeight: "500px" }} className='card-gauge'>
          <Paper elevation={3} style={{ margin: '32px auto', padding: 24 }}>
            <Alert severity="error">
              Failed to load notification preferences: {preferencesError.message}
            </Alert>
          </Paper>
        </div>
      </ResponsiveGridLayout>
    );
  }

  return (
    <ResponsiveGridLayout 
      className="layout" 
      layouts={{ lg: layout }} 
      breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480 }} 
      cols={{ lg: 3, md: 3, sm: 3, xs: 1 }}
      rowHeight={100}
      draggableCancel=".cancelSelectorName"
      isDraggable={false}
      isResizable={false}
      >
      <div key="a" style={{ minHeight: "500px" }} className='card-gauge'>
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', textAlign: 'center' }}>
          <h1>Account Settings</h1>
        </div>
      </div>
      <div key="b" style={{ minHeight: "500px" }} className='card-gauge'>
        <Paper elevation={3} style={{ margin: '32px auto', padding: 24 }}>
          <Box sx={{ padding: 2, border: '1px dashed', borderColor: 'warning.main', borderRadius: 1 }}>
            <Box sx={{ marginBottom: 2 }}>
              <h3 style={{ margin: 0, color: theme.palette.warning.main }}>Notifications</h3>
            </Box>
          <FormGroup>
            <EmailNotificationCard onMessage={handleMessage} />
            <NotificationGroupCard channel="email" onMessage={handleMessage} />
            <SMSNotificationCard onMessage={handleMessage} />
            <NotificationGroupCard channel="sms" onMessage={handleMessage} />
          </FormGroup>
          </Box>
        </Paper>
      </div>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </ResponsiveGridLayout>
  );
};

export default AccountSettingsGrid;